import React, { useEffect, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchWholesaleCustomers,
  createWholesaleCustomer,
  createWholesaleSale,
  deleteWholesaleCustomer,
  selectWholesaleCustomers,
  selectWholesaleCustomersStatus,
  selectWholesaleCustomersError,
} from "../features/wholesale/wholesaleSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/onlineSeller/onlineSellerSlice";
import { selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";

export default function WholesaleCustomersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const customers = useSelector(selectWholesaleCustomers);
  const status = useSelector(selectWholesaleCustomersStatus);
  const error = useSelector(selectWholesaleCustomersError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);
  const auth = useSelector(selectAuth);
  const canDeleteCustomer = can(auth.user, "wholesale_customer", "delete");
  const [deletingId, setDeletingId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    notes: "",
    default_discount_type: "none",
    default_discount_value: "",
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Quick-create sale modal
  const [saleModal, setSaleModal] = useState(null); // customer object
  const [saleStoreId, setSaleStoreId] = useState("");
  const [saleNotes, setSaleNotes] = useState("");
  const [saleLoading, setSaleLoading] = useState(false);
  const [saleError, setSaleError] = useState(null);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchWholesaleCustomers());
    }
  }, [dispatch, status]);

  const handleOpenModal = () => {
    setForm({
      name: "",
      phone: "",
      address: "",
      notes: "",
      default_discount_type: "none",
      default_discount_value: "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError("Название обязательно");
      return;
    }
    if (form.default_discount_type === "percent" || form.default_discount_type === "amount") {
      const v = String(form.default_discount_value ?? "").trim();
      if (v === "" || Number.isNaN(Number(v)) || Number(v) < 0) {
        setFormError("Укажите значение скидки (число ≥ 0)");
        return;
      }
      if (form.default_discount_type === "percent" && Number(v) > 100) {
        setFormError("Процент скидки не может быть больше 100");
        return;
      }
    }
    setFormLoading(true);
    setFormError(null);
    try {
      const payload = { ...form };
      if (payload.default_discount_type === "none") {
        payload.default_discount_value = "0";
      }
      await dispatch(createWholesaleCustomer(payload)).unwrap();
      setModalOpen(false);
    } catch (err) {
      const msg =
        typeof err === "string" ? err : err?.detail || err?.name?.[0] || "Ошибка создания клиента";
      setFormError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenSaleModal = (e, customer) => {
    e.stopPropagation();
    if (storesStatus === "idle") dispatch(fetchStores());
    setSaleModal(customer);
    setSaleStoreId("");
    setSaleNotes("");
    setSaleError(null);
  };

  const handleQuickCreateSale = async () => {
    if (!saleStoreId) {
      setSaleError("Выберите склад");
      return;
    }
    setSaleLoading(true);
    setSaleError(null);
    try {
      const sale = await dispatch(
        createWholesaleSale({
          customer_id: saleModal.id,
          store_id: Number(saleStoreId),
          notes: saleNotes,
        })
      ).unwrap();
      setSaleModal(null);
      navigate(`/wholesale/sales/${sale.id}`);
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка создания продажи";
      setSaleError(msg);
    } finally {
      setSaleLoading(false);
    }
  };

  const handleDeleteCustomer = async (e, customer) => {
    e.stopPropagation();
    if (!window.confirm(`Удалить клиента «${customer.name}»? История продаж и долгов сохранится.`)) return;
    setDeletingId(customer.id);
    try {
      await dispatch(deleteWholesaleCustomer(customer.id)).unwrap();
    } catch (err) {
      const msg = typeof err === "string" ? err : err?.detail || "Ошибка удаления клиента";
      window.alert(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Оптовые клиенты</div>
          <div style={styles.subtitle}>Управление клиентской базой оптовых продаж</div>
        </div>
        <button type="button" onClick={handleOpenModal} style={styles.createBtn}>
          + Новый клиент
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по имени или телефону..."
          style={styles.searchInput}
        />
        <button
          type="button"
          onClick={() => dispatch(fetchWholesaleCustomers())}
          style={styles.refreshBtn}
        >
          Обновить
        </button>
      </div>

      {status === "loading" && <Loading text="Загрузка…" />}
      {status === "failed" && error && <div style={styles.error}>{error}</div>}

      {status === "succeeded" && filtered.length === 0 && (
        <div style={styles.empty}>
          {search ? "Клиенты не найдены по запросу" : "Нет оптовых клиентов. Создайте первого клиента."}
        </div>
      )}

      {filtered.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Клиент</th>
                <th style={styles.th}>Телефон</th>
                <th style={styles.th}>Адрес</th>
                <th style={styles.th}>Скидка</th>
                <th style={styles.th}>Долг</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const debt = Number(c.total_debt) || 0;
                return (
                  <tr
                    key={c.id}
                    style={styles.tr}
                    onClick={() => navigate(`/wholesale/${c.id}`)}
                  >
                    <td style={styles.td}>
                      <div style={styles.customerName}>
                        {c.name}
                        {c.is_active === false && <span style={styles.inactiveBadge}>Неактивен</span>}
                      </div>
                      <div style={styles.customerId}>ID: {c.id}</div>
                    </td>
                    <td style={styles.td}>{c.phone || "—"}</td>
                    <td style={styles.td}>{c.address || "—"}</td>
                    <td style={styles.td}>{formatDefaultDiscount(c)}</td>
                    <td style={styles.td}>
                      <span style={debt > 0 ? styles.debtBadge : styles.paidBadge}>
                        {debt > 0 ? `${debt.toFixed(2)} сом` : "Без долга"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.rowActions}>
                        {c.is_active !== false && (
                          <button
                            type="button"
                            style={styles.newSaleInlineBtn}
                            onClick={(e) => handleOpenSaleModal(e, c)}
                          >
                            + Продажа
                          </button>
                        )}
                        <button
                          type="button"
                          style={styles.viewBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/wholesale/${c.id}`);
                          }}
                        >
                          История
                        </button>
                        {canDeleteCustomer && c.is_active !== false && (
                          <button
                            type="button"
                            style={styles.deleteBtn}
                            disabled={deletingId === c.id}
                            onClick={(e) => handleDeleteCustomer(e, c)}
                          >
                            {deletingId === c.id ? "Удаляю…" : "Удалить"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {saleModal && (
        <div style={styles.overlay} onMouseDown={() => !saleLoading && setSaleModal(null)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Новая продажа — {saleModal.name}</div>
              <button
                type="button"
                onClick={() => !saleLoading && setSaleModal(null)}
                style={styles.modalClose}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              {saleError && <div style={styles.error}>{saleError}</div>}
              <div style={styles.field}>
                <label style={styles.label}>Склад *</label>
                {storesStatus === "loading" && (
                  <Loading inline text="Загрузка складов…" />
                )}
                <Select
                  value={saleStoreId}
                  onChange={setSaleStoreId}
                  options={[
                    { value: "", label: "— Выберите склад —" },
                    ...stores.map((s) => ({
                      value: String(s.id),
                      label: s.name || `Склад #${s.id}`,
                    })),
                  ]}
                  isSearchable={false}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Необязательно"
                  style={styles.textarea}
                  rows={2}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.confirmBtn}
                  onClick={handleQuickCreateSale}
                  disabled={saleLoading}
                >
                  {saleLoading ? "Создание..." : "Создать и перейти →"}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => setSaleModal(null)}
                  disabled={saleLoading}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div style={styles.overlay} onMouseDown={() => setModalOpen(false)}>
          <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Новый оптовый клиент</div>
              <button type="button" onClick={() => setModalOpen(false)} style={styles.modalClose}>
                ×
              </button>
            </div>
            <form style={styles.modalBody} onSubmit={handleCreate}>
              {formError && <div style={styles.error}>{formError}</div>}
              <div style={styles.field}>
                <label style={styles.label}>Название / Имя *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Напр.: ИП Алибеков"
                  style={styles.input}
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Телефон</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+996 700 123 456"
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Адрес</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="г. Бишкек, ул. ..."
                  style={styles.input}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Заметки</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Дополнительная информация..."
                  style={styles.textarea}
                  rows={3}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Скидка по умолчанию (для новых продаж)</label>
                <Select
                  value={form.default_discount_type}
                  onChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      default_discount_type: v,
                      default_discount_value: v === "none" ? "" : f.default_discount_value,
                    }))
                  }
                  options={[
                    { value: "none", label: "Без скидки" },
                    { value: "percent", label: "Процент (%)" },
                    { value: "amount", label: "Фикс. сумма (сом)" },
                  ]}
                  isSearchable={false}
                />
              </div>
              {(form.default_discount_type === "percent" || form.default_discount_type === "amount") && (
                <div style={styles.field}>
                  <label style={styles.label}>
                    {form.default_discount_type === "percent" ? "Процент" : "Сумма, сом"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step={form.default_discount_type === "percent" ? "0.01" : "0.01"}
                    max={form.default_discount_type === "percent" ? "100" : undefined}
                    value={form.default_discount_value}
                    onChange={(e) => setForm((f) => ({ ...f, default_discount_value: e.target.value }))}
                    placeholder={form.default_discount_type === "percent" ? "Напр. 5" : "Напр. 7"}
                    style={styles.input}
                  />
                </div>
              )}
              <div style={styles.modalActions}>
                <button type="submit" style={styles.confirmBtn} disabled={formLoading}>
                  {formLoading ? "Создание..." : "Создать клиента"}
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  style={styles.cancelBtn}
                  disabled={formLoading}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  header: { padding: "16px 24px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", background: "var(--c-bg)", flexShrink: 0 },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subtitle: { fontSize: 15, color: "var(--c-text-muted)", marginTop: 3 },
  createBtn: { padding: "9px 18px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15, whiteSpace: "nowrap" },
  toolbar: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "12px 24px", borderBottom: "1px solid var(--c-border)", background: "var(--c-bg)", flexShrink: 0 },
  searchInput: { flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  refreshBtn: { padding: "8px 16px", borderRadius: 99, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15, whiteSpace: "nowrap" },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  error: { margin: "0 24px", padding: "12px 16px", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, color: "var(--c-danger)", fontSize: 15 },
  empty: { padding: "48px 24px", textAlign: "center", fontSize: 16, color: "var(--c-text-muted)" },
  tableWrap: { margin: "0 24px 24px", border: "1px solid var(--c-border)", borderRadius: 16, overflow: "hidden", background: "var(--c-card)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px" },
  tr: { cursor: "pointer" },
  td: { padding: "11px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", verticalAlign: "middle", color: "var(--c-text)" },
  customerName: { fontWeight: 700, color: "var(--c-text)", fontSize: 16 },
  customerId: { fontSize: 13, color: "var(--c-text-muted)", marginTop: 2, fontFamily: "monospace" },
  debtBadge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 99, background: "rgba(248,113,113,0.14)", border: "1px solid rgba(248,113,113,0.2)", color: "var(--c-danger)", fontSize: 13, fontWeight: 700 },
  paidBadge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", borderRadius: 99, background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.2)", color: "var(--c-success)", fontSize: 13, fontWeight: 700 },
  rowActions: { display: "flex", gap: 8, alignItems: "center" },
  newSaleInlineBtn: { padding: "5px 11px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" },
  viewBtn: { padding: "5px 11px", borderRadius: 99, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" },
  deleteBtn: { padding: "5px 11px", borderRadius: 99, border: "1px solid rgba(248,113,113,0.4)", background: "transparent", color: "var(--c-danger)", cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" },
  inactiveBadge: { marginLeft: 8, display: "inline-flex", alignItems: "center", padding: "2px 7px", borderRadius: 99, background: "rgba(148,163,184,0.14)", border: "1px solid rgba(148,163,184,0.25)", color: "var(--c-text-muted)", fontSize: 12, fontWeight: 700, verticalAlign: "middle" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 },
  modal: { width: "min(480px, 90vw)", maxHeight: "90vh", background: "var(--c-card)", border: "1px solid var(--c-border-light)", borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { padding: "14px 20px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "var(--c-text)" },
  modalClose: { width: 30, height: 30, borderRadius: 16, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center" },
  modalBody: { padding: 20, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--c-text-muted)" },
  input: { padding: "9px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15, fontFamily: "inherit" },
  textarea: { padding: "9px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15, resize: "vertical", fontFamily: "inherit" },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: { padding: "9px 20px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  cancelBtn: { padding: "9px 20px", borderRadius: 99, border: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15 },
};

function formatDefaultDiscount(c) {
  const t = c?.default_discount_type;
  if (!t || t === "none") return "—";
  const n = Number(c?.default_discount_value);
  if (Number.isNaN(n)) return "—";
  if (t === "percent") return `${n}%`;
  if (t === "amount") return `${n.toFixed(2)} сом`;
  return "—";
}
